import { AuthPageShell } from "@/auth/AuthPageShell";
import { useAuth } from "@/auth/AuthProvider";
import { Button, Input } from "@/components/ui";
import { defaultRawRoute } from "@/raw/datasets/registry";
import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";

export function ForgotPasswordPage() {
  const { requestPasswordReset, session, recoveryMode } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (session && !recoveryMode) {
    return <Navigate to={defaultRawRoute} replace />;
  }

  if (recoveryMode) {
    return <Navigate to="/reset-password" replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const result = await requestPasswordReset(email.trim());
    setSubmitting(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    setSent(true);
  };

  return (
    <AuthPageShell title="Reset password">
      {sent ? (
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-text)]">
            If an account exists for <span className="font-medium">{email.trim()}</span>, you will
            receive an email with a link to choose a new password. Check spam if it does not arrive
            within a few minutes.
          </p>
          <Link
            to="/login"
            className="inline-block text-sm font-medium text-[var(--color-accent)] hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-[var(--color-text)]">Email</span>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>

          {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Sending…" : "Send reset link"}
          </Button>

          <p className="text-center text-sm text-[var(--color-text-muted)]">
            <Link to="/login" className="font-medium text-[var(--color-accent)] hover:underline">
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </AuthPageShell>
  );
}
